// ═══════════════════════════════════════════════════════════════════════════
// CARTA INTESTATA AZIENDALE — un solo modello per i documenti stampabili (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// DDT, rapportini e report di commessa escono sulla carta intestata dell'azienda che usa
// Nexiora: logo, ragione sociale, indirizzo, P.IVA / C.F., contatti, colore e posizione del
// logo e piè di pagina scelti in Impostazioni Aziendali → Personalizzazione documenti.
// A schermo il documento è un foglio A4 centrato (con il pulsante Stampa); in stampa e nel
// PDF i margini li dà la pagina (@page), così il contenuto risulta sempre centrato.
// Tutto il testo che arriva dagli utenti passa da esc().
// ═══════════════════════════════════════════════════════════════════════════

export const esc = (t) => String(t ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const COLORE_PREDEFINITO = "#1e1b4b";
const coloreValido = (c) => (/^#[0-9a-f]{6}$/i.test(String(c || "")) ? c : COLORE_PREDEFINITO);

/** Dati dell'azienda per l'intestazione (dal documento organizations/{id}). */
export function datiAzienda(org) {
  const o = org || {};
  return {
    nome: o.nome || o.name || "",
    indirizzo: o.indirizzo || "",
    piva: o.piva || "",
    cf: o.codiceFiscale || "",
    telefono: o.telefono || "",
    email: o.email || "",
    pec: o.pec || "",
    logo: o.logo || "",
    colore: coloreValido(o.coloreDocumenti),
    posizioneLogo: ["sinistra", "centro", "destra"].includes(o.posizioneLogo) ? o.posizioneLogo : "sinistra",
    piePagina: o.piePagina || "",
  };
}

/** CSS comune: foglio A4 centrato a schermo, margini di pagina in stampa, intestazione e piè. */
export function cssCartaIntestata(org) {
  const a = datiAzienda(org);
  return `
    *{box-sizing:border-box;margin:0;padding:0;}
    html,body{background:#e5e7eb;}
    body{font-family:Arial,Helvetica,sans-serif;font-size:11.5px;color:#111827;line-height:1.45;}
    @page{size:A4 portrait;margin:14mm 15mm 14mm;}
    .foglio{width:210mm;min-height:297mm;margin:28px auto;background:#fff;padding:14mm 15mm 12mm;box-shadow:0 4px 24px rgba(17,24,39,.12);border-radius:4px;display:flex;flex-direction:column;}
    .contenuto{flex:1;}
    @media screen and (max-width:860px){.foglio{width:auto;min-height:0;margin:0;padding:18px 16px;border-radius:0;box-shadow:none;}}
    @media print{html,body{background:#fff;}.foglio{width:auto;min-height:0;margin:0;padding:0;box-shadow:none;border-radius:0;}.no-print{display:none!important;}}
    .ci-testata{display:grid;grid-template-columns:1fr auto;gap:20px;align-items:start;padding-bottom:12px;margin-bottom:16px;border-bottom:3px solid ${a.colore};}
    .ci-testata.centro{grid-template-columns:1fr;text-align:center;}
    .ci-testata.centro .ci-doc{text-align:center;border-top:1px solid #e5e7eb;padding-top:10px;}
    .ci-testata.destra .ci-azienda{order:2;text-align:right;}
    .ci-testata.destra .ci-doc{order:1;text-align:left;}
    .ci-logo{max-height:54px;max-width:220px;object-fit:contain;display:inline-block;margin-bottom:6px;}
    .ci-nome{font-size:14px;font-weight:800;color:${a.colore};}
    .ci-dati{font-size:9.5px;color:#4b5563;line-height:1.55;margin-top:2px;}
    .ci-doc{text-align:right;min-width:190px;}
    .ci-tipo{font-size:12px;font-weight:800;color:${a.colore};text-transform:uppercase;letter-spacing:.06em;}
    .ci-numero{font-size:20px;font-weight:900;color:#5b21b6;font-family:'Courier New',monospace;letter-spacing:.5px;margin-top:2px;}
    .ci-numero.bozza{color:#9ca3af;letter-spacing:3px;}
    .ci-data{font-size:10.5px;color:#4b5563;margin-top:2px;}
    .ci-badge{display:inline-block;margin-top:5px;padding:2px 10px;border-radius:10px;font-size:9.5px;font-weight:700;}
    .ci-piede{margin-top:18px;padding-top:7px;border-top:1px solid #e5e7eb;font-size:8.5px;color:#6b7280;text-align:center;line-height:1.5;}
    .ci-piede .ci-legale{white-space:pre-line;color:#4b5563;margin-bottom:2px;}
    .barra-stampa{position:fixed;top:14px;right:16px;z-index:50;display:flex;gap:8px;}
    .barra-stampa button{padding:10px 18px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 4px 12px rgba(0,0,0,.18);font-family:Arial,sans-serif;}
  `;
}

/**
 * Intestazione: azienda (logo + dati) e riquadro del documento (tipo, numero, data, stato).
 * @param {object} doc - { tipo, numero, data, badge:{testo,bg,col}, righe:[html già sicuro] }
 */
// senzaNumero: documenti che non hanno un numero proprio (es. registri): niente riquadro «BOZZA».
export function testataHtml(org, { tipo = "", numero = "", data = "", badge = null, righe = [], senzaNumero = false } = {}) {
  const a = datiAzienda(org);
  const logo = a.logo ? `<img class="ci-logo" src="${esc(a.logo)}" alt=""/>` : "";
  const dati = [
    a.indirizzo,
    [a.piva ? `P.IVA ${a.piva}` : "", a.cf && a.cf !== a.piva ? `C.F. ${a.cf}` : ""].filter(Boolean).join(" · "),
    [a.telefono ? `Tel. ${a.telefono}` : "", a.email, a.pec ? `PEC ${a.pec}` : ""].filter(Boolean).join(" · "),
  ].filter(Boolean).map(esc).join("<br/>");
  return `<div class="ci-testata ${a.posizioneLogo}">
    <div class="ci-azienda">${logo}${a.nome ? `<div class="ci-nome">${esc(a.nome)}</div>` : ""}${dati ? `<div class="ci-dati">${dati}</div>` : ""}</div>
    <div class="ci-doc">
      ${tipo ? `<div class="ci-tipo">${esc(tipo)}</div>` : ""}
      ${senzaNumero ? "" : numero ? `<div class="ci-numero">${esc(numero)}</div>` : `<div class="ci-numero bozza">BOZZA</div><div class="ci-data">numero assegnato all'emissione</div>`}
      ${data ? `<div class="ci-data">${esc(data)}</div>` : ""}
      ${badge ? `<span class="ci-badge" style="background:${badge.bg};color:${badge.col}">${esc(badge.testo)}</span>` : ""}
      ${righe.join("")}
    </div>
  </div>`;
}

/** Piè di pagina: eventuale testo legale dell'azienda + riferimenti del documento. */
export function piedeHtml(org, riferimenti = []) {
  const a = datiAzienda(org);
  return `<div class="ci-piede">${a.piePagina ? `<div class="ci-legale">${esc(a.piePagina)}</div>` : ""}${riferimenti.filter(Boolean).map(esc).join(" · ")}</div>`;
}

/**
 * Apre il documento in una nuova finestra: foglio centrato, pulsante Stampa (non stampato).
 * Restituisce false se il browser blocca la finestra.
 */
export function apriDocumento({ titolo, org, css = "", corpo, piede = "" }) {
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.write(`<!DOCTYPE html><html lang="it"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/>
    <title>${esc(titolo)}</title><style>${cssCartaIntestata(org)}${css}</style></head><body>
    <div class="barra-stampa no-print"><button onclick="window.print()">🖨️ Stampa / Salva PDF</button></div>
    <div class="foglio"><div class="contenuto">${corpo}</div>${piede}</div>
    </body></html>`);
  w.document.close();
  return true;
}

/** Data lunga italiana da "aaaa-mm-gg" ("" se assente). */
export function dataLunga(iso, conGiorno = false) {
  if (!iso) return "";
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  if (isNaN(d)) return "";
  return d.toLocaleDateString("it-IT", { ...(conGiorno ? { weekday: "long" } : {}), day: "numeric", month: "long", year: "numeric" });
}
